import { RuleId } from '@wix/a11y-rules';
import { Box } from '@wix/design-system';
import { useExperiments, useTranslation } from '@wix/fe-essentials/react';
import { GroupExpansionStyle, GroupType, IssueBadge, ToggleType } from '@wix/rules-panel-ui';
import { useSetAtom } from 'jotai';
import { groupBy } from 'lodash';
import React, { useCallback, useRef } from 'react';
import { currentTaskInfoAtom } from '../../atoms/bi-atoms';
import { GroupLoadingSkeleton } from '../../components/wizard-content/group-loading-skeleton';
import { RuleResultsSkeleton } from '../../components/wizard-content/rule-results-skeleton';
import { VerifyPageButton } from '../../components/wizard-content/verify-page-button';
import { usePanelStateAtom } from '../../hooks/rules-state';
import { useBiLogger } from '../../hooks/use-bi-logger';
import { resolveGroupAnalysisState } from '../../utils/resolve-group-analysis-state';
import { useColorContrastRule, useHeadingStructureRule, useInaccessibleComponentsRule, usePageAutoDomOrderRule, useValidStaticPageTitleRule } from '../page-level-rules';
import { useAltTextRule } from '../page-level-rules/use-alt-text-rule';
import { useMediaAlternativesRule } from '../page-level-rules/use-media-alternatives-rule';
import { getNumberOfIssuesInGroup } from '../shared/helpers';
import { useVerify } from '../shared/use-verify';
import { getSubtitles } from './get-subtitles';
export const useBuildPageLevelRuleGroups = () => {
  const isAnalyzingRef = useRef({});
  const biLogger = useBiLogger();
  const {
    t
  } = useTranslation();
  const {
    experiments
  } = useExperiments();
  const {
    getColorContrast
  } = useColorContrastRule();
  const {
    getHeadingStructure
  } = useHeadingStructureRule();
  const {
    getValidStaticPageTitleProps
  } = useValidStaticPageTitleRule();
  const {
    getAltTextRule
  } = useAltTextRule();
  const {
    getMediaAlternatives
  } = useMediaAlternativesRule();
  const {
    getInaccessibleComponents
  } = useInaccessibleComponentsRule();
  const {
    getPageAutoDomOrderRule
  } = usePageAutoDomOrderRule();
  const setCurrentTaskInfo = useSetAtom(currentTaskInfoAtom);
  const {
    getGroupData,
    panelState
  } = usePanelStateAtom();
  const {
    getPagesData
  } = usePanelStateAtom();
  const pageIdToPageData = getPagesData();
  const {
    verify
  } = useVerify({});
  const buildPageLevelRuleGroup = useCallback(props => {
    var _pageIdToPageData$get, _resultsGroupedByRule, _resultsGroupedByRule2, _resultsGroupedByRule3, _resultsGroupedByRule4;
    const {
      pageId,
      onOpen
    } = props;
    const groupData = getGroupData(pageId);
    if (!groupData) {
      throw new Error(`Group data not found for pageId: ${props.pageId}.`);
    }
    const totalPagesToScan = ((_pageIdToPageData$get = pageIdToPageData.get(pageId)) == null || (_pageIdToPageData$get = _pageIdToPageData$get.innerRoutes) == null ? void 0 : _pageIdToPageData$get.length) ?? 0;
    const scannedPagesAmount = groupData.scannedPages ?? 0;
    const {
      title,
      ruleExecutionResults,
      rulesStateMap,
      analysisState
    } = groupData;
    const resultsGroupedByRule = groupBy(ruleExecutionResults, 'ruleId');
    const validStaticPageRuleResults = (_resultsGroupedByRule = resultsGroupedByRule[RuleId.VALID_STATIC_PAGE_TITLE]) == null ? void 0 : _resultsGroupedByRule[0];
    const headingStructurePageRuleResults = (_resultsGroupedByRule2 = resultsGroupedByRule[RuleId.HEADING_STRUCTURE]) == null ? void 0 : _resultsGroupedByRule2[0];
    const staticAltTextRuleResults = resultsGroupedByRule[RuleId.STATIC_ALT_TEXT];
    const colorContrastRuleResults = (_resultsGroupedByRule3 = resultsGroupedByRule[RuleId.COLOR_CONTRAST]) == null ? void 0 : _resultsGroupedByRule3[0];
    const mediaAlternativesRuleResults = resultsGroupedByRule[RuleId.MEDIA_ALTERNATIVES];
    const inaccessibleComponentsRuleResults = resultsGroupedByRule[RuleId.INACCESSIBLE_COMPONENTS];
    const pageAutoDomOrderRuleResults = (_resultsGroupedByRule4 = resultsGroupedByRule[RuleId.PAGE_AUTO_DOM_ORDER]) == null ? void 0 : _resultsGroupedByRule4[0];
    const uiRuleResultsById = {
      [RuleId.VALID_STATIC_PAGE_TITLE]: validStaticPageRuleResults ? getValidStaticPageTitleProps({
        ...props,
        pageId,
        ruleExecutionResult: validStaticPageRuleResults
      }) : null,
      [RuleId.INACCESSIBLE_COMPONENTS]: inaccessibleComponentsRuleResults ? getInaccessibleComponents({
        ...props,
        pageId,
        ruleExecutionResult: inaccessibleComponentsRuleResults
      }) : null,
      [RuleId.HEADING_STRUCTURE]: headingStructurePageRuleResults ? getHeadingStructure({
        ...props,
        pageId,
        ruleExecutionResult: headingStructurePageRuleResults
      }) : null,
      [RuleId.STATIC_ALT_TEXT]: getAltTextRule({
        ...props,
        pageId,
        ruleExecutionResult: staticAltTextRuleResults ?? []
      }),
      [RuleId.COLOR_CONTRAST]: colorContrastRuleResults ? getColorContrast({
        ...props,
        pageId,
        ruleExecutionResult: colorContrastRuleResults
      }) : null,
      [RuleId.MEDIA_ALTERNATIVES]: mediaAlternativesRuleResults ? getMediaAlternatives({
        ...props,
        pageId,
        ruleExecutionResult: mediaAlternativesRuleResults
      }) : null,
      [RuleId.INACCESSIBLE_LAYOUT]: null,
      [RuleId.PAGE_AUTO_DOM_ORDER]: pageAutoDomOrderRuleResults ? getPageAutoDomOrderRule({
        ...props,
        pageId,
        ruleExecutionResult: pageAutoDomOrderRuleResults
      }) : null
    };
    const filteredUiRuleResults = Object.fromEntries(Object.entries(uiRuleResultsById).filter(_ref => {
      let [, value] = _ref;
      return value !== null && (!value.enabledByExperiment || experiments.enabled(value.enabledByExperiment));
    }));
    const ruleResults = Object.values(filteredUiRuleResults);
    const numberOfIssues = getNumberOfIssuesInGroup(rulesStateMap);
    const handleVerifyClick = async () => {
      var _panelState$globalSec;
      await verify(groupData, [...(((_panelState$globalSec = panelState.globalSectionComponentsDataMap) == null ? void 0 : _panelState$globalSec.values().map(_ref2 => {
        let {
          componentId
        } = _ref2;
        return componentId;
      })) ?? [])]);
    };
    const {
      isFirstAnalysis,
      isAnalyzeFailed,
      isPendingResults,
      isAnalyzing: isAnalyzingState,
      isFetchResultsFailed
    } = resolveGroupAnalysisState(analysisState);
    const isAnalyzing = isAnalyzingState || isFirstAnalysis;
    if (isAnalyzingRef.current[pageId] && !isAnalyzing && groupData.analysisData) {
      var _groupData$analysisDa;
      biLogger.pageScanFinished({
        analysisJobId: (groupData == null || (_groupData$analysisDa = groupData.analysisData) == null ? void 0 : _groupData$analysisDa.analysisJobId) ?? '',
        issuesCount: isAnalyzeFailed ? -1 : numberOfIssues,
        pageId,
        pageType: groupData.itemType
      });
    }
    isAnalyzingRef.current[pageId] = isAnalyzing;
    return {
      disabled: isFirstAnalysis || isAnalyzeFailed,
      expansionMode: GroupExpansionStyle.collapsible,
      groupId: pageId,
      groupType: GroupType.StaticPage,
      isFetchResultsFailed,
      isLoading: isPendingResults || isAnalyzing,
      onGroupToggle: function (toggleType, groupIndex, initiatedByUser) {
        if (initiatedByUser === void 0) {
          initiatedByUser = false;
        }
        setCurrentTaskInfo(taskInfo => ({
          ...taskInfo,
          page_id: pageId,
          page_index: groupIndex,
          page_type: GroupType.StaticPage
        }));
        biLogger.pageExpandCollapse({
          initiatedByUser,
          pageId,
          pageIndex: groupIndex,
          pageType: GroupType.StaticPage,
          ruleResults,
          toggleType
        });
        toggleType === ToggleType.Expand && onOpen();
      },
      ruleGroupContent: {
        badge: isAnalyzeFailed ? /*#__PURE__*/React.createElement(IssueBadge, {
          label: t('accessibility-wizard.rule-group.issue-count-badge.label.scan-failed')
        }) : numberOfIssues !== 0 && !isAnalyzing ? /*#__PURE__*/React.createElement(IssueBadge, {
          label: t('accessibility-wizard.rule-group.issue-count-badge.label', {
            numberOfIssues
          })
        }) : undefined,
        badgeTooltipContent: isAnalyzeFailed ? t('accessibility-wizard.rule-group.issue-count-badge.tooltip-content.scan-failed') : undefined,
        collapsedNode: isFirstAnalysis ? /*#__PURE__*/React.createElement(GroupLoadingSkeleton, null) : isAnalyzeFailed || isAnalyzing ? /*#__PURE__*/React.createElement(Box, {
          padding: "0 3px"
        }, /*#__PURE__*/React.createElement(VerifyPageButton, {
          failedTaskCount: numberOfIssues,
          isLoading: isAnalyzing,
          pageId: pageId,
          taskCount: ruleResults.length,
          onClick: handleVerifyClick
        })) : undefined,
        expandNode: /*#__PURE__*/React.createElement(VerifyPageButton, {
          failedTaskCount: numberOfIssues,
          isLoading: isAnalyzing,
          pageId: pageId,
          taskCount: ruleResults.length,
          onClick: handleVerifyClick
        }),
        groupLabel: title,
        subtitles: getSubtitles({
          groupData,
          scannedPagesAmount,
          t,
          totalPagesToScan
        })
      },
      ruleResults,
      ruleResultsSkeletonComponent: /*#__PURE__*/React.createElement(RuleResultsSkeleton, {
        analysisState: analysisState,
        pageId: pageId
      })
    };
  }, [getInaccessibleComponents, getGroupData, pageIdToPageData, getValidStaticPageTitleProps, getHeadingStructure, getAltTextRule, getColorContrast, getMediaAlternatives, getPageAutoDomOrderRule, t, experiments, verify, panelState.globalSectionComponentsDataMap, setCurrentTaskInfo, biLogger]);
  return {
    buildPageLevelRuleGroup
  };
};
//# sourceMappingURL=use-build-page-level-rule-group.js.map